import React from 'react';
import { Audio, useVideoConfig } from 'remotion';
import { LayerContainer } from './types';

/**
 * MainAudioLayerContainer
 *
 * Manages the main audio track for the timeline.
 * This layer contains all audio from the Trebble editor including:
 * - Transcribed voice audio
 * - Sound effects added in Trebble
 * - Background music added in Trebble
 * - Any audio from video clips
 *
 * This is separate from animation sound effects (SoundPlayer) which are
 * synchronized with specific animations.
 */
export class MainAudioLayerContainer implements LayerContainer {
    audioUrl: string | null = null;
    duration: number = 0;
    volume: number = 1.0;

    constructor(audioUrl?: string, duration?: number, volume?: number) {
        if (audioUrl) {
            this.audioUrl = audioUrl;
        }
        if (duration !== undefined) {
            this.duration = duration;
        }
        if (volume !== undefined) {
            this.volume = volume;
        }
    }

    /**
     * Set the main audio track
     * @param audioUrl - URL to the audio file (typically a rendered WAV from Trebble editor)
     * @param duration - Duration of the audio in seconds
     * @param volume - Volume level (0-1), defaults to 1.0
     */
    setAudio(audioUrl: string, duration: number, volume: number = 1.0): void {
        this.audioUrl = audioUrl;
        this.duration = duration;
        this.volume = volume;

        if (process.env.NODE_ENV === 'development') {
            console.log('[MainAudioLayer] Audio set:', {
                audioUrl,
                duration,
                volume,
                hasAudio: !!this.audioUrl
            });
        }
    }

    /**
     * Render the main audio track using Remotion Audio component
     */
    render(): React.ReactNode {
        // No audio to render
        if (!this.audioUrl) {
            if (process.env.NODE_ENV === 'development') {
                console.log('[MainAudioLayer] No audio URL set, skipping render');
            }
            return null;
        }

        // Validate duration
        if (this.duration <= 0) {
            console.warn('[MainAudioLayer] Invalid duration:', this.duration);
            return null;
        }

        if (process.env.NODE_ENV === 'development') {
            console.log('[MainAudioLayer] Rendering audio:', {
                audioUrl: this.audioUrl,
                duration: this.duration,
                volume: this.volume
            });
        }

        // Use functional component to access video config
        const AudioWithConfig = () => {
            const { fps } = useVideoConfig();
            return (
                <Audio
                    src={this.audioUrl!}
                    volume={this.volume}
                    startFrom={0}
                    endAt={Math.round(this.duration * fps)} // Use composition fps
                />
            );
        };

        return <AudioWithConfig />;
    }
}
